import { useEffect, useState, useRef, useCallback } from 'react';
import moment from 'moment';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Form from 'react-bootstrap/Form';
import Tab from 'react-bootstrap/Tab';
import Tabs from 'react-bootstrap/Tabs';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSms as iconSMS, faChild as iconChild } from '@fortawesome/free-solid-svg-icons';
import { useUserContext } from '../../../hooks/userHook';
import Loading from '../../../components/Loading';
import Delete, { DELETE_TYPES } from '../../../components/ScheduleActions/Delete';
import Publish, { PUBLISH_TYPES } from '../../../components/ScheduleActions/Publish';
import BookingAction from '../../../components/ScheduleActions/Booking';
import { Booking, Schedule } from "../../../models";
import { getSchedule, getBooking } from "../../../data/api";
import './AdminSchedule.scss';

type AdminScheduleProps = {
  id: string;
  [x: string]: any;
}

export default function AdminSchedule({ id, ...rest }: AdminScheduleProps) {
  const { user } = useUserContext();
  const loadingRef = useRef<any>(null);
  const [event, setEvent] = useState<Schedule | null>(null);
  const [booking, setBooking] = useState<Booking | null>(null);
  const [tabKey, setTabKey] = useState<number>(0);

  const toggleSpinner = useCallback((loading: boolean) => loadingRef?.current?.show(loading), [loadingRef]);

  const getEventInfo = async () => {
    toggleSpinner(true);
    const scheduleData = await getSchedule(id);

    if (scheduleData.success) {
      if (scheduleData?.data?.booked) {
        const bookingData = await getBooking(id, user?.token);
        bookingData?.success && setBooking(bookingData.data);
      }
      setEvent(scheduleData.data || null);
    }
    toggleSpinner(false);
  };

  useEffect(() => {
    getEventInfo();
  }, []);

  return (
    <div id="schedule-admin-container" {...rest} className="container">
      <Loading ref={loadingRef} />

      <h6 className="fullimpact-grey">
        Event Details
        <span
          onClick={() => navigator.clipboard.writeText(event?.id || '')}
          className="fw-lighter fs-6 text-muted ms-2"
        >
          {event?.id}
        </span>
      </h6>
      <hr />
      <Row className="py-2">
        <Col lg="3" className="fw-light pe-2">Time</Col>
        <Col lg="9" className="fw-bold">
          {`${moment(event?.startDateTimeUTC).local().format('llll')} -  ${moment(event?.endDateTimeUTC).local().format('LT')}`}
        </Col>
      </Row>
      <Row className="py-2">
        <Col lg="3" className="fw-light pe-2">Booked</Col>
        <Col lg="9" className="fw-bold">
          <Form.Check
            type="checkbox"
            checked={!!(event?.booked)}
            onChange={() => null}
            readOnly={true}
          />
        </Col>
      </Row>
      {
        !!(event?.booked) && (
          <>
            <Row className="py-2">
              <Col lg="3" className="fw-light pe-2"><span>{`Particpant${booking?.participant && booking.participant.length > 1 ? 's' : ''}`}</span></Col>
              <Col lg="9" className="fw-bold">
                {booking?.participant && booking.participant.length > 1 ? (
                  <ol>
                    {booking?.participant.map((participant, index) => (
                      <li key={index}>
                        {`${participant.participantFirstName || ''} ${participant.participantLastName || ''}`}
                        {booking?.underAge && <FontAwesomeIcon icon={iconChild} className="ms-2" />}
                      </li>
                    ))}
                  </ol>
                )
                  : (
                    <>
                      {`${booking?.participant?.[0]?.participantFirstName || ''} ${booking?.participant?.[0]?.participantLastName || ''}`}
                      {booking?.underAge && <FontAwesomeIcon icon={iconChild} className="ms-2" />}
                    </>
                  )}
              </Col>
            </Row>
            {booking?.underAge && (
              <Row className="py-2">
                <Col lg="3" className="fw-light pe-2"><span>Guardian</span></Col>
                <Col lg="9" className="fw-bold">
                  {`${booking?.guardianFirstName} ${booking?.guardianLastName}`}
                </Col>
              </Row>
            )}
            <Row className="py-2">
              <Col lg="3" className="fw-light pe-2 d-flex align-items-center">Telephone</Col>
              <Col lg="9" className="fw-bold d-flex align-items-center">
                {booking?.telephone && <a href={`tel:${booking.telephone}`}>{booking.telephone.replace(/(\d{3})(\d{3})(\d{4})/, "($1) $2-$3")}</a>}
                {booking?.telephone && <a href={`sms:${booking?.telephone}`} className="ms-3"><FontAwesomeIcon icon={iconSMS} size="2x" /></a>}
              </Col>
            </Row>
            <Row className="py-2">
              <Col lg="3" className="fw-light pe-2">Email</Col>
              <Col lg="9" className="fw-bold">
                {booking?.email && <a href={`mailto:${booking.email}`}>{booking.email}</a>}
              </Col>
            </Row>
          </>
        )
      }

      <h6 className="fullimpact-grey mt-4 pt-4">Event Actions</h6>
      <hr />

      <div id="schedule-action-container" className="mb-4">
        <Tabs
          id="schedule-action-tabs"
          activeKey={tabKey}
          onSelect={(key) => setTabKey(parseInt(key || '0', 10))}
          className="mb-4"
        >
          <Tab eventKey={0} title="Publish">
            <div className="schedule-tab-container">
              <div className="d-flex justify-content-around flex-row align-items-center">
                {event?.id && <Publish
                  id="schedule-event-publish"
                  size="lg"
                  scheduleId={event.id}
                  publishType={PUBLISH_TYPES.Single}
                  published={event.published}
                  successCallback={getEventInfo}
                  className="me-4"
                />}
                {event?.recurrenceId && <Publish
                  id="schedule-event-publish-all"
                  scheduleId={event.recurrenceId}
                  publishType={PUBLISH_TYPES.Recurrence}
                  published={event.published}
                  successCallback={getEventInfo}
                />}
              </div>
            </div>
          </Tab>
          <Tab eventKey={1} title="Booking">
            <div className="schedule-tab-container">
              <div className="d-flex justify-content-around flex-row align-items-center">
                {event?.id && <BookingAction
                  id="schedule-event-booking"
                  size="lg"
                  scheduleId={event.id}
                  booked={event?.booked}
                  successCallback={getEventInfo}
                  className="me-4"
                />}
              </div>
            </div>
          </Tab>
          <Tab eventKey={2} title="Delete">
            <div className="schedule-tab-container">
              <div className="d-flex justify-content-around flex-row align-items-center">
                {event?.id && <Delete
                  id="schedule-event-delete"
                  size="lg"
                  scheduleId={event?.id}
                  deleteType={DELETE_TYPES.Single}
                  successCallback={getEventInfo}
                  className="me-4"
                />}
                {event?.recurrenceId && <Delete
                  id="schedule-event-delete-all"
                  scheduleId={event?.recurrenceId}
                  deleteType={DELETE_TYPES.Recurrence}
                  successCallback={getEventInfo}
                />}
              </div>
            </div>
          </Tab>
        </Tabs>
      </div>

    </div >
  );
}